/* eslint-disable @typescript-eslint/no-explicit-any */
import { PUP_DISPLAY_NAME } from "../config"
import { Trello } from "../types/trello"
import { apiClient } from "../api/api"
import { BoardMode, getStoredCardCustomer, getStoredTrackedTime } from "../storage"

/**
 * Whether this card has anything to show, from Trello's own card data alone.
 *
 * What counts depends on the board's mode: a customer-linking board cares about
 * the linked customer, a time-tracking board about logged time. Each copy
 * carries the card it was written for, because copying a card duplicates its
 * shared data while the API-side records — keyed by card id — stay with the
 * original.
 */
const hasMirroredContent = async (
  t: Trello.PowerUp.IFrame,
  mode: BoardMode
): Promise<boolean> => {
  const contextCardId = t.getContext().card
  const belongsToThisCard = (cardId?: string) =>
    !cardId || !contextCardId || cardId === contextCardId

  if (mode === "time") {
    const trackedTime = await getStoredTrackedTime(t)
    return !!trackedTime && belongsToThisCard(trackedTime.cardId)
  }

  const customer = await getStoredCardCustomer(t)
  return !!customer?.id && belongsToThisCard(customer.cardId)
}

/**
 * The same question asked of the API, for cards the mirror can't answer for.
 *
 * Only for customer linking, and only because of history: cards linked before
 * the mirror existed have a CardCustomerLink and no local copy, and the
 * backfill that used to fix them ran inside the card back — which would no
 * longer open for them, so nothing would ever fix them again.
 *
 * Time tracking needs no such fallback. Entries only ever arrive through this
 * power-up, which writes the mirror as it saves, so the mirror is authoritative
 * and a time board pays nothing per card open.
 *
 * A failure answers "nothing", not "something": an outage should not put an
 * empty panel on every card back. Note this costs a request on every open of a
 * card with *no* customer — such a card never gets a mirror, so it can't be
 * remembered as empty. That is the price of not stranding the legacy ones.
 */
const hasLegacyCustomerLink = async (
  t: Trello.PowerUp.IFrame,
  cardId: string
): Promise<boolean> => {
  try {
    const customer = await apiClient.getCardCustomer(t, cardId)
    return !!customer?.customerId
  } catch {
    return false
  }
}

/**
 * The card back section, or nothing at all.
 *
 * Nothing is the common case: most cards have no linked customer and no tracked
 * time, and an empty panel on every card back is noise. Linking a customer and
 * logging the first entry both happen from card buttons, which are always
 * there; this section appears once one of them has been used.
 */
export async function getCardBackSection(
  t: Trello.PowerUp.IFrame,
  props: any,
  mode: BoardMode
): Promise<Trello.PowerUp.CardBackSection | null> {
  const cardId = t.getContext().card
  const hasContent =
    (await hasMirroredContent(t, mode)) ||
    (mode === "customers" && !!cardId && (await hasLegacyCustomerLink(t, cardId)))
  if (!hasContent) return null

  return {
    title: PUP_DISPLAY_NAME,
    // A single URL, not a dark/light pair — Trello's type allows one string
    // here. It recolors this slot by the alpha channel, and both glyphs share
    // an alpha, so which file it points at makes no visible difference.
    icon: props.baseUrl + props.logo.light,
    content: {
      type: "iframe",
      // The header can run to three rows (customer, contact, totals) before the
      // scrolling time-entry list starts, so keep enough room for a few entries.
      height: 380,
      url: t.signUrl("./card-back-section.html")
    }
  }
}
